import {
  fetchAdministrationMarketplaceProductActivity,
  type AdministrationMarketplaceActivityQuery,
} from "../../../api/administrationMarketplace";
import { createKeyedResourceLoader } from "../../state/keyedResourceLoader";
import type { MarketplaceAdministrationSlice, MarketplaceProductActivitySlice } from "./types";

const loader = createKeyedResourceLoader();
export const emptyMarketplaceActivitySummary = {
  total: 0,
  comparisonPercent: null,
  comparisonPeriod: null,
  configuration: 0,
  productChanges: 0,
  pricingChanges: 0,
  inventory: 0,
  published: 0,
  withdrawn: 0,
  checkoutChanges: 0,
  acquired: 0,
  acquiredTotals: [],
  claims: 0,
  claimsSubmitted: 0,
  claimsResolved: 0,
};

export const productActivityKey = (query: AdministrationMarketplaceActivityQuery) => JSON.stringify({
  productId: query.productId,
  opportunityId: query.opportunityId || "",
  query: query.query?.trim() || "",
  groups: [...(query.groups || [])].sort(),
  sources: [...(query.sources || [])].sort(),
  severities: [...(query.severities || [])].sort(),
  dateFrom: query.dateFrom || "",
  dateTo: query.dateTo || "",
  sort: query.sort || "newest",
  limit: query.limit || 50,
});

export const marketplaceProductActivityInitialState = { productActivities: {} };
export const resetMarketplaceProductActivityRequests = loader.clear;

export const createMarketplaceProductActivitySlice:
MarketplaceAdministrationSlice<MarketplaceProductActivitySlice> = (set, get) => ({
  ...marketplaceProductActivityInitialState,
  loadProductActivity: (query, options = {}) => {
    const key = productActivityKey(query);
    const current = get().productActivities[key];
    const append = Boolean(options.append && query.cursor);
    return loader.load({
      key: `${key}:${query.cursor || "first"}`,
      force: options.force,
      cached: !append && Boolean(current?.hydrated),
      request: () => fetchAdministrationMarketplaceProductActivity(query),
      onStart: () => set((state) => ({ productActivities: { ...state.productActivities, [key]: {
        query: { ...query, cursor: null },
        items: current?.items || [],
        nextCursor: current?.nextCursor || null,
        totalCount: current?.totalCount || 0,
        summary: current?.summary || emptyMarketplaceActivitySummary,
        hydrated: current?.hydrated || false,
        loading: !append,
        loadingMore: append,
        error: "",
      } } })),
      onSuccess: (page) => set((state) => ({ productActivities: { ...state.productActivities, [key]: {
        query: { ...query, cursor: null },
        ...page,
        items: append
          ? [...(state.productActivities[key]?.items || []), ...page.items.filter((item) => !(state.productActivities[key]?.items || []).some((existing) => existing.id === item.id))]
          : page.items,
        hydrated: true,
        loading: false,
        loadingMore: false,
        error: "",
      } } })),
      onError: (error) => set((state) => ({ productActivities: { ...state.productActivities, [key]: {
        ...(state.productActivities[key] || { query, items: [], nextCursor: null, totalCount: 0, summary: emptyMarketplaceActivitySummary, hydrated: false }),
        loading: false,
        loadingMore: false,
        error,
      } } })),
      onFinish: () => set((state) => state.productActivities[key] ? ({ productActivities: { ...state.productActivities, [key]: { ...state.productActivities[key], loading: false, loadingMore: false } } }) : state),
      errorMessage: "Unable to load Marketplace product activity.",
    });
  },
  refreshProductActivities: () => Promise.all(
    Object.values(get().productActivities)
      .filter((entry) => entry.hydrated)
      .map((entry) => get().loadProductActivity({ ...entry.query, cursor: null }, { force: true })),
  ).then(() => undefined),
});
